import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, Edit, Trash2 } from "lucide-react";
import { AddProspectDialog } from "@/components/prospects/AddProspectDialog";
import { EditProspectDialog } from "@/components/prospects/EditProspectDialog";
import { DeleteConfirmDialog } from "@/components/prospects/DeleteConfirmDialog";
import { PageTitle } from "@/components/common/PageTitle";
import { useProspects } from "@/hooks/useProspects";
import { PIPELINE_STAGES } from "@/lib/constants";

export function Prospects() {
  const { prospects, isLoading } = useProspects();

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStageLabel = (stage: string) => {
    return PIPELINE_STAGES.find(s => s.value === stage)?.label || stage;
  };

  const getStageVariant = (stage: string): "default" | "secondary" | "outline" => {
    switch (stage) {
      case 'new':
        return 'outline';
      case 'contacted':
      case 'qualified':
        return 'secondary';
      case 'interested':
      case 'committed':
      case 'participant':
        return 'default';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageTitle title="Prospects" />
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Prospects</h1>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <p className="text-muted-foreground">Loading prospects...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle title="Prospects" />
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Prospects</h1>
        <div className="flex space-x-2">
          <Button variant="outline">
            <Upload className="mr-2 h-4 w-4" />
            Import CSV
          </Button>
          <AddProspectDialog />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Prospects ({prospects.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prospects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No prospects found. Add your first prospect to get started.
                  </TableCell>
                </TableRow>
              ) : (
                prospects.map((prospect) => (
                  <TableRow key={prospect._id}>
                    <TableCell className="font-medium">
                      {prospect.company}
                    </TableCell>
                    <TableCell>{prospect.contactName}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {prospect.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {prospect.phone || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStageVariant(prospect.stage)}>
                        {getStageLabel(prospect.stage)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(prospect.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <EditProspectDialog 
                          prospect={prospect}
                          trigger={
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          }
                        />
                        <DeleteConfirmDialog 
                          prospect={prospect}
                          trigger={
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          }
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}